import { memo, type ReactNode } from 'react';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Image,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { DeckLikeCount } from './DeckLikeCount';
import type { Deck } from '../../types/deck';
import { DECK_FORMAT_LABELS, DECK_LIMITS, mainDeckCount } from '../../types/deck';
import { DomainIcon } from '../icons/FacetIcons';
import { BlurredArtBackground } from '../BlurredArtBackground';
import { domainGradientCss } from '../../utils/domainGradient';

export interface DeckSearchSuggestionRowProps {
  deck: Deck;
  id?: string;
  active?: boolean;
  onActivate?: () => void;
  onHover?: () => void;
  disabled?: boolean;
  /** Replaces default stats column (likes, main-deck count, format label). */
  trailing?: ReactNode;
}

export const DeckSearchSuggestionRow = memo(function DeckSearchSuggestionRow({
  deck,
  id,
  active = false,
  onActivate,
  onHover,
  disabled = false,
  trailing,
}: DeckSearchSuggestionRowProps) {
  const legend = deck.legend;
  const thumb = legend?.thumbnailUrl || legend?.imageUrl;
  const domains = legend?.domain ?? [];
  const gradient = domainGradientCss(domains, 'card');
  const formatCode = deck.formatCode;
  const formatLabel = DECK_FORMAT_LABELS[formatCode] ?? formatCode;
  const dc = mainDeckCount(deck);

  const dn = deck.ownerDisplayName?.trim() ?? '';
  const authorLine =
    dn && dn.toLowerCase() !== deck.ownerHandle.toLowerCase()
      ? `${dn} · @${deck.ownerHandle}`
      : `@${deck.ownerHandle}`;

  const interactive = Boolean(onActivate) && !disabled;

  return (
    <Box
      id={id}
      role={interactive ? 'option' : undefined}
      aria-selected={interactive ? active : undefined}
      as={interactive ? 'button' : 'div'}
      cursor={interactive ? 'pointer' : disabled ? 'not-allowed' : undefined}
      w="100%"
      textAlign="left"
      display="flex"
      alignItems="stretch"
      borderRadius="md"
      borderWidth="hairline"
      borderColor={active ? 'accentMuted' : 'border'}
      bg={active ? 'bg.emphasized' : 'bgElevated'}
      outline="none"
      overflow="hidden"
      px={2}
      py={1.5}
      gap={2}
      minH="64px"
      position="relative"
      opacity={disabled ? 0.65 : 1}
      _hover={
        interactive
          ? {
              bg: active ? undefined : 'bg.emphasized',
              borderColor: active ? undefined : 'accentMuted',
            }
          : undefined
      }
      onMouseEnter={interactive ? onHover : undefined}
      onMouseDown={
        interactive
          ? (e) => {
              e.preventDefault();
              onActivate?.();
            }
          : undefined
      }
    >
      <BlurredArtBackground
        src={thumb}
        domains={domains}
        opacity={active ? 0.08 : 0.06}
        blur="8px"
      />
      {gradient !== 'none' && (
        <Box position="absolute" inset={0} background={gradient} opacity={active ? 0.12 : 0.1} pointerEvents="none" />
      )}
      <Box
        flexShrink={0}
        w="52px"
        alignSelf="stretch"
        borderRadius="md"
        overflow="hidden"
        position="relative"
        bg="surface"
      >
        {thumb ? (
          <Image
            src={thumb}
            alt=""
            w="100%"
            h="100%"
            minH="54px"
            objectFit="cover"
            objectPosition="top center"
            style={{ transform: 'scale(1.08)', transformOrigin: 'top center' }}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        ) : (
          <Flex align="center" justify="center" h="full" px={1}>
            <Text fontSize="2xs" color="textMuted" textAlign="center">
              —
            </Text>
          </Flex>
        )}
      </Box>

      <Flex direction="column" flex={1} minW={0} gap={0.5} position="relative" zIndex={1} justify="center">
        <HStack gap={2} align="flex-start">
          <Text fontSize="sm" fontWeight="semibold" lineClamp={1} flex={1}>
            {deck.title || 'Untitled'}
          </Text>
          <Badge size="xs" colorPalette="gray" variant="outline" flexShrink={0}>
            {formatCode}
          </Badge>
        </HStack>
        {legend?.name && (
          <Text fontSize="2xs" color="textMuted" lineClamp={1} textTransform="uppercase" letterSpacing="wide">
            {legend.name}
          </Text>
        )}
        <Text fontSize="xs" color="textMuted" lineClamp={1}>
          {authorLine}
        </Text>
        {domains.length > 0 && (
          <HStack gap={1} mt={0.5}>
            {domains.map((d) => (
              <DomainIcon key={d} value={d} size={14} />
            ))}
          </HStack>
        )}
      </Flex>

      <Flex direction="column" justify="center" gap={1} align="flex-end" flexShrink={0} position="relative" zIndex={1}>
        {trailing ?? (
          <>
            <DeckLikeCount count={deck.likeCount} />
            <Text fontSize="2xs" color="textMuted">
              {dc}/{DECK_LIMITS.MAIN_DECK_SIZE}
            </Text>
            <Text fontSize="2xs" color="textMuted" title={formatLabel}>
              {formatLabel}
            </Text>
          </>
        )}
      </Flex>
    </Box>
  );
});

export function DeckSearchSuggestionRowLoadingTrailing() {
  return <Spinner size="xs" color="accent" />;
}
